/**
 * Global Prefetch Progress Banner
 *
 * Non-blocking banner showing background prefetch progress
 * - Top-right corner, dismissible
 * - Auto-hides after completion
 * - Shows progress bar, spinner, and current task
 */

import { useState, useEffect } from 'react';
import { Loader2, CheckCircle, X } from 'lucide-react';

interface GlobalPrefetchBannerProps {
  isLoading: boolean;
  progress: number; // 0-100
  currentTask: string;
  error: string | null;
  isComplete: boolean;
}

export function GlobalPrefetchBanner({
  isLoading,
  progress,
  currentTask,
  error,
  isComplete,
}: GlobalPrefetchBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Auto-show banner when loading starts
  useEffect(() => {
    if (isLoading && !isDismissed) {
      setShowBanner(true);
    }
  }, [isLoading, isDismissed]);

  // Auto-hide after 3 seconds when complete
  useEffect(() => {
    if (isComplete && showBanner && !error) {
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, showBanner, error]);

  if (!showBanner || (isDismissed && !isLoading)) {
    return null;
  }

  const isError = !!error;
  const bgColor = isError ? 'bg-red-50' : isComplete ? 'bg-green-50' : 'bg-blue-50';
  const borderColor = isError ? 'border-red-200' : isComplete ? 'border-green-200' : 'border-blue-200';
  const textColor = isError ? 'text-red-700' : isComplete ? 'text-green-700' : 'text-blue-700';
  const progressBgColor = isError ? 'bg-red-200' : isComplete ? 'bg-green-200' : 'bg-blue-200';
  const progressFillColor = isError ? 'bg-red-600' : isComplete ? 'bg-green-600' : 'bg-blue-600';

  return (
    <div
      className={`fixed top-4 right-4 max-w-sm w-full z-50 rounded-lg border ${bgColor} ${borderColor} p-4 shadow-lg transition-all duration-300`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1">
          {isLoading && !isComplete ? (
            <Loader2 className={`w-5 h-5 animate-spin ${textColor}`} />
          ) : isComplete && !isError ? (
            <CheckCircle className={`w-5 h-5 ${textColor}`} />
          ) : (
            <div className={`w-5 h-5 rounded-full ${textColor}`} />
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${textColor}`}>
              {isError ? 'Prefetch error' : isComplete ? 'Ready for offline!' : 'Setting up offline mode'}
            </p>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={() => setIsDismissed(true)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      {!isError && (
        <div className="mb-2">
          <div className={`h-1.5 rounded-full overflow-hidden ${progressBgColor}`}>
            <div
              className={`h-full ${progressFillColor} transition-all duration-300`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Current task / Error message */}
      <div className="flex items-center justify-between">
        <p className={`text-xs ${textColor} truncate flex-1`}>
          {isError ? error : currentTask}
        </p>
        {!isError && (
          <span className={`text-xs font-medium ${textColor} ml-2 flex-shrink-0`}>
            {progress}%
          </span>
        )}
      </div>

      {/* Helpful message on completion */}
      {isComplete && !isError && (
        <p className={`text-xs ${textColor} mt-2 opacity-75`}>
          You can now work offline with all data cached
        </p>
      )}
    </div>
  );
}
